import { useState, useEffect, FormEvent } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import { verifyResetCode, completePasswordReset } from '../firebase';
import { useTheme } from '../contexts/ThemeContext';
import FormInput from '../components/ui/FormInput';

type PageState = 'loading' | 'form' | 'submitting' | 'success' | 'error';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { theme } = useTheme();

  const oobCode = searchParams.get('oobCode') || '';

  const [state, setState] = useState<PageState>('loading');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!oobCode) {
      setError('Ongeldige link. Geen reset code gevonden.');
      setState('error');
      return;
    }

    verifyResetCode(oobCode)
      .then((userEmail) => {
        setEmail(userEmail);
        setState('form');
      })
      .catch(() => {
        setError('Deze link is verlopen of al gebruikt. Vraag een nieuwe link aan.');
        setState('error');
      });
  }, [oobCode]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (password.length < 6) {
      setError('Wachtwoord moet minimaal 6 tekens zijn.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Wachtwoorden komen niet overeen.');
      return;
    }

    setError('');
    setState('submitting');

    try {
      await completePasswordReset(oobCode, password);
      setState('success');
      setTimeout(() => {
        navigate(`/?email=${encodeURIComponent(email)}`);
      }, 2000);
    } catch {
      setError('Er ging iets mis. De link is mogelijk verlopen.');
      setState('form');
    }
  };

  const toggleButton = (show: boolean, onToggle: () => void) => (
    <button
      type="button"
      onClick={onToggle}
      className="text-muted-foreground hover:text-foreground transition-colors"
    >
      {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
    </button>
  );

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md animate-enter">
        {/* Logo */}
        <div className="text-center mb-8">
          {theme.logo ? (
            <img src={theme.logo} alt={theme.name} className="h-12 mx-auto mb-2" />
          ) : (
            <h1 className="text-4xl font-bold tracking-tight">
              <span className="text-primary">{theme.name.toLowerCase()}</span>
            </h1>
          )}
        </div>

        <div className="glass-card p-8">
          {/* Loading state */}
          {state === 'loading' && (
            <div className="text-center py-8">
              <Loader2 className="w-10 h-10 text-primary animate-spin mx-auto mb-4" />
              <p className="text-muted-foreground">Link verifieren...</p>
            </div>
          )}

          {/* Error state */}
          {state === 'error' && (
            <div className="text-center py-4">
              <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-foreground mb-2">Link verlopen</h2>
              <p className="text-muted-foreground mb-6">{error}</p>
              <button
                onClick={() => navigate('/')}
                className="btn-primary px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-xl transition-colors"
              >
                Terug naar inloggen
              </button>
            </div>
          )}

          {/* Success state */}
          {state === 'success' && (
            <div className="text-center py-4">
              <CheckCircle className="w-12 h-12 text-success mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-foreground mb-2">Wachtwoord ingesteld!</h2>
              <p className="text-muted-foreground mb-2">Je wachtwoord is succesvol gewijzigd.</p>
              <p className="text-muted-foreground/60 text-sm">Je wordt doorgestuurd naar de inlogpagina...</p>
            </div>
          )}

          {/* Form state */}
          {(state === 'form' || state === 'submitting') && (
            <>
              <h2 className="text-xl font-semibold text-foreground mb-2 text-center">
                Wachtwoord instellen
              </h2>
              <p className="text-muted-foreground text-sm text-center mb-6">
                voor <span className="text-foreground font-medium">{email}</span>
              </p>

              {error && (
                <div className="mb-4 p-3 bg-destructive/10 border border-destructive/30 rounded-lg flex items-center gap-2 text-destructive text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <FormInput
                  icon={Lock}
                  label="Nieuw wachtwoord"
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimaal 6 tekens"
                  required
                  minLength={6}
                  autoFocus
                  suffix={toggleButton(showPassword, () => setShowPassword(!showPassword))}
                />

                <FormInput
                  icon={Lock}
                  label="Bevestig wachtwoord"
                  type={showConfirm ? 'text' : 'password'}
                  id="confirm"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Herhaal wachtwoord"
                  required
                  minLength={6}
                  suffix={toggleButton(showConfirm, () => setShowConfirm(!showConfirm))}
                />

                <button
                  type="submit"
                  disabled={state === 'submitting'}
                  className="btn-primary w-full py-3 bg-primary hover:bg-primary/90 disabled:bg-primary/50 text-primary-foreground font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                  {state === 'submitting' ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Bezig...
                    </>
                  ) : (
                    'Wachtwoord instellen'
                  )}
                </button>
              </form>

              <div className="mt-4 text-center">
                <button
                  onClick={() => navigate('/')}
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  Terug naar inloggen
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
